<script setup>
import { useAsyncState } from "@vueuse/core";
import { createDefaultPaginationInfo } from "@/utils/fnHelper.js";
import { useDict } from "@/utils/dict.js";
import { auditLog } from "@/api/log/log.js";
import { queryAuditRejectFileList } from "@/api/order/orderCenter.js";
import ImageListPreview from "@/components/ImagePreview/ImageListPreview.vue";
import { GeneralFileUtils } from "@/hooks/useBenaGenUploadFile.js";
import { negate } from "lodash-es";

const operationOrderLogVisible = ref(false);

const operationOrderLogQueryParams = reactive({
  recordId: "",
  tableName: "",
  auditType: 1,
  ...createDefaultPaginationInfo()
});

const operationOrderLogFetcher = useAsyncState(
  () => auditLog(operationOrderLogQueryParams),
  {
    data: {
      records: [],
      total: 0
    }
  },
  {
    immediate: false,
    resetOnExecute: false,
    async onSuccess() {
      //  这些模式下需要请求图片
      const pList = operationOrderLogFetcher.state.value.data.records.map(async (v) => {
        const { data: files = [] } = await queryAuditRejectFileList(v.logId);

        const pics = files.filter(GeneralFileUtils.isImageType);
        const attachments = files.filter(negate(GeneralFileUtils.isImageType));

        console.log(pics);
        return {
          ...v,
          pics,
          attachments
        };
      });

      operationOrderLogFetcher.state.value.data.records = await Promise.all(pList);
      triggerRef(operationOrderLogFetcher.state);
    }
  }
);

const openOperationDialog = () => {
  Object.assign(operationOrderLogQueryParams, createDefaultPaginationInfo());
  operationOrderLogFetcher.execute();
  operationOrderLogVisible.value = true;
};

defineExpose({
  //  打开订单审核日志
  openOrderAuditLog({ orderId }) {
    mode.value = modeMap.订单;
    Object.assign(operationOrderLogQueryParams, {
      recordId: orderId,
      tableName: "bn_sc_order",
      auditType: 1
    });
    openOperationDialog();
  },

  //  打开送样单入库审核日志
  openStoreAuditLog({ orderId }) {
    mode.value = modeMap.送样单入库;
    Object.assign(operationOrderLogQueryParams, {
      recordId: orderId,
      tableName: "bn_sc_order",
      auditType: 2
    });
    openOperationDialog();
  },

  openCrossUnionResultEnterAuditLog({ executeId }) {
    mode.value = modeMap.交联结果录入;
    Object.assign(operationOrderLogQueryParams, {
      recordId: executeId,
      tableName: "bn_sc_cross_link_order_execute",
      auditType: 1
    });
    openOperationDialog();
  },

  openExtractResultEnterAuditLog({ executeId }) {
    mode.value = modeMap.提取结果录入;
    Object.assign(operationOrderLogQueryParams, {
      recordId: executeId,
      tableName: "bn_sc_extract_detection_order_execute",
      auditType: 1
    });
    openOperationDialog();
  },

  openCrossUnionResultMergedAuditLog({ reportId }, auditType) {
    mode.value = modeMap.交联结果合并;
    Object.assign(operationOrderLogQueryParams, {
      recordId: reportId,
      tableName: "bn_sc_cross_link_order_report",
      auditType: 1
    });
    openOperationDialog();
  },

  openExtractResultMergedAuditLog({ reportId }, auditType) {
    mode.value = modeMap.提取结果合并;
    Object.assign(operationOrderLogQueryParams, {
      recordId: reportId,
      tableName: "bn_sc_extract_detection_order_report",
      auditType: 1
    });
    openOperationDialog();
  },

  // 新增建库|测序生成结果审核
  openBuildLibResultEnterAuditLog({ resultId }) {
    console.log(resultId);
    mode.value = modeMap.建库生成结果;
    Object.assign(operationOrderLogQueryParams, {
      recordId: resultId,
      tableName: "bn_sc_build_library_order_result",
      auditType: 1
    });
    openOperationDialog();
  },
  openSequenceResultEnterAuditLog({ resultId }) {
    console.log(resultId);
    mode.value = modeMap.测序生成结果;
    Object.assign(operationOrderLogQueryParams, {
      recordId: resultId,
      tableName: "bn_sc_sequence_order_result",
      auditType: 1
    });
    openOperationDialog();
  },
  // 新增建库|测序最终审核
  openBuildLibResultMergedAuditLog({ resultId }) {
    mode.value = modeMap.建库最终审核;
    Object.assign(operationOrderLogQueryParams, {
      recordId: resultId,
      tableName: "bn_sc_build_library_order_result",
      auditType: 1
    });
    openOperationDialog();
  },
  openSequenceResultMergedAuditLog({ resultId }) {
    mode.value = modeMap.测序最终审核;
    Object.assign(operationOrderLogQueryParams, {
      recordId: resultId,
      tableName: "bn_sc_sequence_order_result",
      auditType: 1
    });
    openOperationDialog();
  }
});

const modeMap = {
  Order: "订单",
  订单: "Order",
  SampleOrderEnter: "送样单入库",
  送样单入库: "SampleOrderEnter",
  ExtractResultEnterAuditLog: "提取结果录入",
  提取结果录入: "ExtractResultEnterAuditLog",
  CrossUnionResultEnterAuditLog: "交联结果录入",
  交联结果录入: "CrossUnionResultEnterAuditLog",

  //  结果生成
  交联结果合并: "CrossUnionResultMerge",
  提取结果合并: "ExtractResultMerge",

  // 新增建库|测序结果录入审核
  建库生成结果: "BuildLibResultEnterAuditLog",
  BuildLibResultEnterAuditLog: "建库生成结果",
  测序生成结果: "SequenceResultEnterAuditLog",
  SequenceResultEnterAuditLog: "测序生成结果",
  // 新增建库|测序最终审核
  建库最终审核: "BuildLibResultMergedAuditLog",
  BuildLibResultMergedAuditLog: "建库最终审核",
  测序最终审核: "SequenceResultMergedAuditLog",
  SequenceResultMergedAuditLog: "测序最终审核"
};

const mode = ref("");

function useSolitudeDict(dictName) {
  return unref(useDict(dictName)[dictName]);
}

const statusOpts = computed(() => {
  switch (toValue(mode)) {
    case modeMap.订单: {
      return useSolitudeDict("order_customer_delivery_sample_status");
    }
    case modeMap.送样单入库: {
      return useSolitudeDict("order_customer_delivery_sample_storage_status");
    }
    case modeMap.提取结果录入: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.提取结果合并: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.交联结果录入: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.交联结果合并: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.建库生成结果: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.建库最终审核: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.测序生成结果: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    case modeMap.测序最终审核: {
      return useSolitudeDict("experiment_execute_order_report_result_audit_status");
    }
    default:
      return Array.of();
  }
});
</script>

<template>
  <el-drawer v-model="operationOrderLogVisible" append-to-body size="800px">
    <template #header>
      <div>
        <el-tag type="primary">审核日志</el-tag>
      </div>
    </template>
    <el-table
      v-loading="operationOrderLogFetcher.isLoading.value"
      ref="dragTable"
      :data="operationOrderLogFetcher.state.value.data.records"
      row-key="logId"
    >
      <template #empty>
        <el-alert :closable="false" class="my-24 w-full" type="info">暂无审核记录</el-alert>
      </template>
      <el-table-column label="序号" type="index" width="55" />
      <el-table-column label="操作人" prop="auditUserName" width="140" />
      <el-table-column label="操作" prop="auditStatus" width="130">
        <template #default="{ row }">
          <dict-tag :options="statusOpts" :value="row.auditStatus" />
        </template>
      </el-table-column>
      <el-table-column label="时间" prop="auditTime" width="180" />
      <el-table-column label="备注" prop="remark" />
      <el-table-column label="图片" prop="pics">
        <template #default="{ row: { pics = [] } }">
          <ImageListPreview :pic-list="pics" />
        </template>
      </el-table-column>
      <el-table-column label="附件" prop="attachments" width="200">
        <template #default="{ row: { attachments = [] } }">
          <!--          <ImageListPreview :pic-list="pics" />-->
          <div class="flex flex-col items-start gap-y-2">
            <el-link
              @click="() => GeneralFileUtils.downloadFile(benaGenFileDTO)"
              v-for="(benaGenFileDTO, i) in attachments"
              type="primary"
              :key="benaGenFileDTO.id"
              class="leading-none"
            >
              ({{ i + 1 }}) {{ benaGenFileDTO.showName }}
              <!--              <FilePreview :bena-gen-file="benaGenFileDTO" />-->
            </el-link>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <template #footer>
      <pagination
        v-model:limit="operationOrderLogQueryParams.pageSize"
        v-model:page="operationOrderLogQueryParams.pageNum"
        :total="operationOrderLogFetcher.state.value.data.total"
        @pagination="operationOrderLogFetcher.execute"
      />
    </template>
  </el-drawer>
</template>

<style lang="scss" scoped></style>
